// Bargeld außerhalb von Verkäufen (Einlage, Entnahme, Geldtransit, Auszahlung) und das Kassenbuch einer Kasse: jede
// Bargeldbewegung mit ihrem Beleg. Schulungs- und Demomodus (lib/labels.ts: POS_BANNER).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zTillEcho, zTillProof } from '../lib/offline/types'
import { zAmountCents, zCashKind, zDay, zReason, zRegisterRef } from './common'

export const cashMove = defineContract({
  name: 'pos.cash.move',
  title: 'Bargeld einlegen oder entnehmen',
  description: 'Bucht Wechselgeld aus dem Tresor, einen Geldtransit, eine Privateinlage oder -entnahme, eine Aus- oder Einzahlung in der offenen Schicht – mit Grund, Belegnummer und Vorgangsnummer.',
  llm: { description: 'Bucht eine Bargeldbewegung in der offenen Schicht einer Kasse (Betrag in Cent, immer positiv; die Richtung ergibt sich aus der Art). Nur an der Kasse selbst – das Geld wird dabei bewegt.', keywords: ['Einlage', 'Entnahme', 'Geldtransit', 'Abschöpfung'] },
  input: z
    .strictObject({
      shiftId: zId,
      kind: zCashKind,
      amountCents: zAmountCents,
      reason: zReason,
      /** nur Auszahlung/Einzahlung: Steuersatz der Ausgabe bzw. Einnahme in Basispunkten (19 %, 7 %, 0 %) */
      taxRateBp: z.union([z.literal(1900), z.literal(700), z.literal(0)], { error: 'Bitte wählen Sie 19 %, 7 % oder 0 %.' }).optional(),
      /** Pflicht an Offline-Kassen, mit nextBonId (docs/kasse-offline/SPEZIFIKATION.md §14.2) */
      till: zTillProof.optional(),
    })
    .refine((value) => value.taxRateBp === undefined || value.kind === 'payout' || value.kind === 'payin', { error: 'Einen Steuersatz haben nur Auszahlung und Einzahlung.', path: ['taxRateBp'] }),
  output: z.object({ id: zId, number: z.string(), signedCents: z.int(), bonId: z.int(), message: z.string(), till: zTillEcho.optional() }),
  permission: 'pos.cash.move',
  risk: 'financial',
  scope: 'record',
  assistant: 'never',
})

export const zCashBookEntry = z.object({
  /** stabiler Schlüssel der Zeile */
  key: z.string(),
  at: z.date(),
  businessDate: z.string(),
  shiftId: zId,
  /** float = Wechselgeld beim Öffnen · payment = Barzahlung eines Bons · movement = Einlage/Entnahme · count = Differenz bei der Zählung */
  kind: z.enum(['float', 'payment', 'movement', 'count']),
  label: z.string(),
  /** Belegnummer: Bon, Rücknahme oder Bargeldbewegung */
  number: z.string().nullable(),
  /** Beleg, auf den die Zeile verweist (Bon oder Rücknahme) */
  documentId: zId.nullable(),
  documentType: z.string().nullable(),
  text: z.string().nullable(),
  amountCents: z.int(),
  /** Kassenbestand nach dieser Zeile */
  balanceCents: z.int(),
  byName: z.string().nullable(),
})

export const cashBook = defineContract({
  name: 'pos.cash.book',
  title: 'Kassenbuch anzeigen',
  description: 'Kassenbuch einer Kasse: jede Bargeldbewegung mit Beleg und laufendem Bestand.',
  llm: {
    description:
      'Liefert das Kassenbuch EINER Kasse für einen Zeitraum (Standard: die letzten 7 Geschäftstage) oder eine ' +
      'Schicht: Wechselgeld beim Öffnen, Barzahlungen der Bons (Erstattungen negativ), Einlagen, Entnahmen, ' +
      'Geldtransit, Differenzen der Zählung – je Zeile Betrag in Cent, Belegnummer und Kassenbestand danach. ' +
      'Kartenzahlungen und Gutscheine stehen nicht im Kassenbuch (pos.shift.get nennt sie je Zahlart). Kassen ' +
      'findet pos.register.list. Daten aus dem Schulungs- und Demomodus der Kasse.',
    keywords: ['Kassenbuch', 'Bargeld', 'Kassenbestand', 'Entnahmen', 'Einlagen'],
  },
  input: z.strictObject({ registerId: zId, shiftId: zId.optional(), businessDateFrom: zDay.optional(), businessDateTo: zDay.optional() }),
  output: z.object({
    register: zRegisterRef,
    from: z.string().nullable(),
    to: z.string().nullable(),
    entries: z.array(zCashBookEntry),
    /** Bestand nach der letzten Zeile */
    balanceCents: z.int(),
    truncated: z.boolean(),
    mark: z.string(),
  }),
  permission: 'pos.shift.read',
  risk: 'read',
  scope: 'record',
})
